import React, { useMemo, useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Search, X, Loader2, Tag } from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import FloatingActions from '@/components/site/FloatingActions';
import Placeholder from '@/components/site/Placeholder';
import PageFAQ, { buildFaqJsonLd } from '@/components/site/PageFAQ';
import { useCatalog } from '@/hooks/useCatalog';
import useSEO from '@/hooks/useSEO';
import { productImageUrl } from '@/lib/imageUrl';

// Shared FAQ set — most category pages get the same core answers,
// with 1–2 category-specific overrides mixed in via ALIAS_SEO.faqs.
const CORE_FAQ = {
  ships: {
    q: 'Do you ship Indian clothes across the USA?',
    a: 'Yes — we ship pan-USA. Local customers around Morrisville, Raleigh, Durham, Cary, Apex and Chapel Hill can pick up in-store or arrange delivery. For customers elsewhere in the US, please WhatsApp us and we\'ll walk you through sizing and shipping.',
  },
  size: {
    q: 'How do I choose the right size?',
    a: 'Most pieces are available in XS – XXL with custom sizing on request. Message us your bust / waist / hip measurements on WhatsApp and we\'ll recommend the best fit and, if needed, tailor the outfit before it ships.',
  },
  visit: {
    q: 'Can I visit your boutique in Morrisville, NC?',
    a: 'Absolutely — the showroom is at 158 Liberty Rose Dr, Morrisville, NC 27560. We\'re open daily 4:30 PM – 8:30 PM. Please call or WhatsApp 984-377-9835 to book a private appointment.',
  },
  wedding: {
    q: 'What outfits are best for Indian weddings?',
    a: 'For the bride: hand-embroidered lehengas or heavy Banarasi sarees. For guests: cocktail lehengas, sequin sarees or Anarkali suits. Sangeet & mehendi lean lighter — organza sarees, sharara sets or gowns work beautifully.',
  },
  rentals: {
    q: 'Do you rent Indian outfits?',
    a: 'We are primarily a retail boutique — outfits are curated for purchase. If you\'d like to be notified about anything else, please WhatsApp us directly.',
  },
  tailoring: {
    q: 'Do you offer tailoring and alterations?',
    a: 'Yes — in-house tailoring is available for every piece we sell. Bring the outfit to our Morrisville boutique and we\'ll perfect the fit before your event.',
  },
};

// SEO-friendly copy per URL alias — keyword-targeted for Phase 2 (USA-wide).
const ALIAS_SEO = {
  // Existing aliases — refreshed with USA keyword targeting
  sarees: {
    title: 'Indian Sarees Online USA · Banarasi & Designer | Trendy Closet',
    description: 'Shop Indian sarees online across the USA. Banarasi silk, Kanjivaram, organza & pre-drape designer sarees. Boutique in Morrisville, NC — ships nationwide.',
    heading: 'Indian Sarees Online in the USA',
    intro: 'From heirloom Banarasi silks to lightweight pre-drape designer sarees, our curated collection is loved across the USA and hand-picked in Morrisville, NC.',
    keyword: 'Indian Sarees Online USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.wedding, CORE_FAQ.tailoring, CORE_FAQ.visit],
  },
  lehengas: {
    title: 'Designer Lehengas USA · Bridal & Party | Trendy Closet',
    description: 'Designer lehengas for weddings, sangeets & parties — shipped across the USA. Bridal red velvet, cocktail sequin & mirror-work styles. Boutique in Morrisville, NC.',
    heading: 'Designer Lehengas in the USA',
    intro: 'Hand-picked designer lehengas for the whole wedding week — from heavy bridal red velvet to lightweight cocktail sequin styles. Shipped anywhere in the USA.',
    keyword: 'Designer Lehenga USA',
    faqs: [CORE_FAQ.wedding, CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.visit],
  },
  'bridal-wear': {
    title: 'Indian Bridal Wear USA · Wedding Lehengas & Sarees | Trendy Closet',
    description: 'Heirloom Indian bridal wear for USA brides — wedding lehengas, phera sarees & reception outfits. Owner-styled at Trendy Closet, Morrisville NC.',
    heading: 'Indian Bridal Wear in the USA',
    intro: 'Every bridal edit is styled with our owner, who has helped brides across the USA look their absolute best on the biggest day. Book a private consultation on WhatsApp.',
    keyword: 'Indian Bridal Wear USA',
    faqs: [CORE_FAQ.wedding, CORE_FAQ.visit, CORE_FAQ.tailoring, CORE_FAQ.ships, CORE_FAQ.size],
  },
  'kurti-sets': {
    title: 'Indian Kurti Sets USA · Anarkali & Palazzo | Trendy Closet',
    description: 'Buy Indian kurti sets online in the USA — Anarkalis, palazzo suits, sharara sets & cord sets for festive and everyday wear. Ships nationwide from Morrisville, NC.',
    heading: 'Indian Kurti Sets in the USA',
    intro: 'Everyday festive kurta sets, Anarkalis, palazzo suits and cord sets — hand-picked for Indian celebrations across the USA, from Diwali to Eid to Karva Chauth.',
    keyword: 'Indian Kurti Sets USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.visit],
  },
  // Same content served under /category/kurtis for URL consistency
  kurtis: {
    title: 'Indian Kurti Sets USA · Anarkali & Palazzo | Trendy Closet',
    description: 'Buy Indian kurti sets online in the USA — Anarkalis, palazzo suits, sharara sets & cord sets for festive and everyday wear. Ships nationwide from Morrisville, NC.',
    heading: 'Indian Kurti Sets in the USA',
    intro: 'Everyday festive kurta sets, Anarkalis, palazzo suits and cord sets — hand-picked for Indian celebrations across the USA, from Diwali to Eid to Karva Chauth.',
    keyword: 'Indian Kurti Sets USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.visit],
  },
  'party-wear': {
    title: 'Indian Party Wear Gowns USA · Cocktail & Sangeet | Trendy Closet',
    description: 'Indo-western gowns, cocktail lehengas & statement Indian party wear — shipped across the USA. Boutique showroom in Morrisville, NC.',
    heading: 'Indian Party Wear Gowns in the USA',
    intro: 'Indo-western gowns, cocktail lehengas and statement Indian party wear for receptions, sangeets and festive nights — shipped anywhere in the US.',
    keyword: 'Indian Party Wear Gowns USA',
    faqs: [CORE_FAQ.wedding, CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring],
  },
  // New Phase-2 aliases
  shararas: {
    title: 'Sharara Suits USA · Sharara & Gharara Sets | Trendy Closet',
    description: 'Shop sharara suits online in the USA — festive & bridal sharara sets, gharara suits and Anarkali-sharara pairings. Ships nationwide from Morrisville, NC.',
    heading: 'Sharara Suits in the USA',
    intro: 'Sharara suits are back — flowy silhouettes, delicate embroidery and versatile styling. Our sharara & gharara sets ship across the USA from our Morrisville boutique.',
    keyword: 'Sharara Suits USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.wedding, CORE_FAQ.visit],
  },
  gowns: {
    title: 'Indian Party Wear Gowns USA · Indo-Western | Trendy Closet',
    description: 'Indian party wear gowns online in the USA — Indo-western gowns, cocktail gowns and reception silhouettes. Boutique in Morrisville, NC ships nationwide.',
    heading: 'Indian Party Wear Gowns in the USA',
    intro: 'Fusion gowns that pair the drama of Indian embroidery with modern Western silhouettes — perfect for cocktail nights, receptions and NYE parties.',
    keyword: 'Indian Party Wear Gowns USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.wedding],
  },
  men: {
    title: "Men's Kurta Pajama USA · Sherwani & Indo-Western | Trendy Closet",
    description: "Men's Indian wear online across the USA — kurta pajama, sherwanis, Nehru jackets and Indo-western sets. Boutique in Morrisville, NC ships nationwide.",
    heading: "Men's Kurta Pajama in the USA",
    intro: "From understated everyday kurtas to statement wedding sherwanis, our men's collection ships across the USA. Custom sizing available on request.",
    keyword: "Men's Kurta Pajama USA",
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.wedding, CORE_FAQ.visit],
  },
  kids: {
    title: 'Kids Indian Clothing USA · Ethnic Wear for Boys & Girls | Trendy Closet',
    description: 'Kids Indian clothing online in the USA — girls lehengas, boys kurta sets and family-matching outfits for Diwali, weddings & festivals. Ships nationwide.',
    heading: 'Kids Indian Clothing in the USA',
    intro: 'Adorable ethnic wear for kids — girls lehengas, boys kurta-pajama sets and family-matching outfits for weddings, Diwali and every celebration in between.',
    keyword: 'Kids Indian Clothing USA',
    faqs: [CORE_FAQ.ships, CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.visit],
  },
  'plus-size': {
    title: 'Plus Size Indian Clothing USA · XL to XXXL | Trendy Closet',
    description: 'Plus size Indian clothing across the USA — lehengas, sarees and kurti sets available in XL, XXL, XXXL and custom sizing. Boutique in Morrisville, NC.',
    heading: 'Plus Size Indian Clothing in the USA',
    intro: 'Every piece in the closet can be tailored up to XXXL, and many styles are already sized inclusively. Message us on WhatsApp for personalised sizing.',
    keyword: 'Plus Size Indian Clothing USA',
    faqs: [CORE_FAQ.size, CORE_FAQ.tailoring, CORE_FAQ.ships, CORE_FAQ.visit],
  },
};

// URL alias → catalog category key mapping
const ALIAS_TO_CATEGORY = {
  sarees: 'sarees',
  lehengas: 'lehengas',
  'bridal-wear': 'bridal',
  'kurti-sets': 'family',
  kurtis: 'family',
  'party-wear': 'gowns',
  shararas: 'sharara-suits',
  gowns: 'gowns',
  men: 'mens',
  kids: 'kids',
  // /plus-size intentionally has no fixed category — it shows every product
  // framed under "plus-size availability" copy.
  'plus-size': '',
};

/**
 * Unified browse page.
 * - /category/:key → filters by category
 * - /search?q=xxx → free-text search across all products
 * - /sarees, /lehengas, /bridal-wear, /kurti-sets, /party-wear → SEO alias routes
 *   (pass `fixedKey` from App.js router)
 */
export default function Browse({ mode = 'category', fixedKey }) {
  const params = useParams();
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const { products, categories, loading } = useCatalog();

  // Resolve the target category key based on either a URL param or a fixed alias
  const aliasKey = fixedKey || params.key || '';
  const key = ALIAS_TO_CATEGORY[aliasKey] || aliasKey;
  const aliasSeo = ALIAS_SEO[aliasKey];

  const [q, setQ] = useState(sp.get('q') || '');
  const [tag, setTag] = useState(sp.get('tag') || '');

  useEffect(() => {
    // keep URL query in sync (only for search mode)
    if (mode === 'search') {
      const params = {};
      if (q) params.q = q;
      if (tag) params.tag = tag;
      setSp(params, { replace: true });
    }
  }, [q, tag, mode, setSp]);

  const category = mode === 'category' ? categories.find((c) => c.key === key) : null;

  // Per-page SEO (title / description / canonical / structured data).
  // Canonical always points to the preferred `/category/{key}` URL (never the
  // alias) so Google consolidates all alias URLs into one page in search.
  // Alias landing pages that don't map to a real category (e.g. /plus-size,
  // /indian-clothing-usa) keep a self-referencing canonical.
  const SITE_ORIGIN = 'https://trendyclosetusa.com';
  const canonical = (() => {
    if (mode !== 'category') {
      return typeof window !== 'undefined'
        ? `${SITE_ORIGIN}${window.location.pathname}`
        : undefined;
    }
    if (category) return `${SITE_ORIGIN}/category/${category.key}`;
    // No matching DB category → self-reference the current URL (e.g. /plus-size, /indian-clothing-usa)
    return typeof window !== 'undefined'
      ? `${SITE_ORIGIN}${window.location.pathname}`
      : undefined;
  })();

  const jsonLd = useMemo(() => {
    if (mode !== 'category') return undefined;
    const arr = [];
    // CollectionPage schema — prefer the alias landing page's rich name/description if set,
    // otherwise use the resolved category label.
    const collectionName = aliasSeo?.heading || (category ? category.label : null);
    const collectionDescription = aliasSeo?.description
      || (category ? `${category.label} at Trendy Closet — Indian ethnic wear boutique in Morrisville, NC. Ships across the USA.` : null);
    if (collectionName && canonical) {
      arr.push({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: collectionName,
        description: collectionDescription,
        url: canonical,
        about: aliasSeo?.keyword || collectionName,
        isPartOf: { '@type': 'WebSite', name: 'Trendy Closet', url: SITE_ORIGIN },
      });
    }
    if (aliasSeo?.faqs?.length) arr.push(buildFaqJsonLd(aliasSeo.faqs));
    // Breadcrumbs for real category pages
    if (category && canonical) {
      arr.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: category.label, item: canonical },
        ],
      });
    }
    return arr.length ? arr : undefined;
  }, [mode, aliasSeo, canonical, category]);

  // Invalid category URL → noindex (SPA still returns HTTP 200)
  const invalidCategory = mode === 'category' && !loading && !category && !aliasSeo;
  // Search result pages should never be indexed — they're infinitely many combinations
  const isSearch = mode === 'search';
  // Empty category → thin/empty content; noindex until at least one product is added.
  // Kept `follow` (via useSEO's noindex,follow default) so internal links keep passing equity.
  const emptyCategory = mode === 'category' && !loading && category
    && products.filter((p) => p.category === category.key).length === 0;

  useSEO({
    title:
      mode === 'search'
        ? 'Search · Indian Clothing USA | Trendy Closet'
        : aliasSeo?.title
          || (category
            ? `${category.label} — Indian Boutique in Morrisville, NC | Trendy Closet USA`
            : 'Category not found | Trendy Closet'),
    description:
      aliasSeo?.description
        || (category
          ? `Shop ${category.label.toLowerCase()} at Trendy Closet, Morrisville NC. Owner-curated Indian ethnic wear, in-house tailoring & US-wide shipping. Serving Cary, Raleigh, Durham.`
          : 'Search premium Indian ethnic wear — bridal lehengas, sarees, kurti sets & party wear at Trendy Closet, Morrisville NC.'),
    canonical,
    jsonLd,
    noindex: invalidCategory || isSearch || emptyCategory,
  });

  const filtered = useMemo(() => {
    let list = products;
    // Alias with a mapped category → filter to that category.
    // Alias with '' mapping (e.g. /plus-size) → show every product.
    if (mode === 'category' && key) list = list.filter((p) => p.category === key);
    if (q.trim()) {
      const rx = new RegExp(q.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      list = list.filter((p) => {
        const cat = categories.find((c) => c.key === p.category)?.label || '';
        return (
          rx.test(p.label || '') ||
          rx.test(p.sub || '') ||
          rx.test(p.description || '') ||
          rx.test(p.fabric || '') ||
          rx.test(p.color || '') ||
          rx.test(p.occasion || '') ||
          rx.test(cat) ||
          (p.tags || []).some((t) => rx.test(t))
        );
      });
    }
    if (tag) list = list.filter((p) => (p.tags || []).includes(tag));
    return list;
  }, [products, categories, key, q, tag, mode]);

  const tagCloud = useMemo(() => {
    const counts = {};
    (mode === 'category' ? products.filter((p) => p.category === key) : products).forEach((p) =>
      (p.tags || []).forEach((t) => { counts[t] = (counts[t] || 0) + 1; })
    );
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 20);
  }, [products, key, mode]);

  const title = mode === 'category'
    ? (aliasSeo?.heading || category?.label || 'Collection')
    : 'Search';
  const overline = mode === 'category' ? 'Collection' : q ? `Results for “${q}”` : 'Search the Closet';
  const clearAll = () => { setQ(''); setTag(''); };

  if (mode === 'category' && !loading && !category && !aliasSeo) {
    return (
      <main className="min-h-screen bg-ivory text-charcoal">
        <Navbar />
        <section className="container-luxe pt-40 pb-20 text-center">
          <span className="overline">Not Found</span>
          <h1 className="font-display text-5xl mt-4">Collection not found.</h1>
          <p className="mt-4 text-charcoal/70">It may have been renamed or removed.</p>
          <Link to="/" className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-burgundy text-ivory text-xs uppercase tracking-[0.22em] hover:bg-maroon">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main data-testid={mode === 'category' ? 'category-page' : 'search-page'} className="min-h-screen bg-ivory text-charcoal">
      <Navbar />

      <section className="pt-28 lg:pt-32 pb-24 lg:pb-28">
        <div className="container-luxe">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-charcoal/55 mb-8">
            <Link to="/" className="hover:text-maroon">Home</Link>
            <span>/</span>
            <span className="text-charcoal">{title}</span>
          </nav>

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
            <div>
              <span className="overline">{overline}</span>
              <h1 className="font-display text-4xl lg:text-6xl text-charcoal mt-3 leading-[1.05] tracking-tight">
                {mode === 'category' ? (
                  aliasSeo ? (
                    aliasSeo.heading
                      .split(' in the USA')
                      .reduce((acc, part, idx) => (
                        idx === 0
                          ? <>{part} in the <em className="text-maroon not-italic font-medium">USA</em></>
                          : acc
                      ), null)
                  ) : (
                    <>Curated <em className="text-maroon not-italic font-medium">{title.toLowerCase()}</em>.</>
                  )
                ) : (
                  <>Find your <em className="text-maroon not-italic font-medium">next piece</em>.</>
                )}
              </h1>
              <div className="mt-5 h-px w-16 bg-gold" />
              {aliasSeo?.intro && (
                <p className="mt-6 max-w-2xl text-charcoal/80 leading-relaxed">
                  {aliasSeo.intro}
                </p>
              )}
              {aliasSeo?.description && !aliasSeo.intro && (
                <p className="mt-6 max-w-2xl text-charcoal/75 leading-relaxed">
                  {aliasSeo.description}
                </p>
              )}
            </div>
            {mode === 'category' && (
              <p className="max-w-md text-charcoal/70 leading-relaxed">
                Tap any card to see the details and enquire on WhatsApp.
              </p>
            )}
          </div>
          {/* Search + filters */}
          <div className="border-y border-brandborder/70 py-5 mb-10 flex flex-col lg:flex-row gap-4 lg:items-center">
            <div className="flex items-center gap-3 flex-1 max-w-xl border border-brandborder focus-within:border-maroon px-4 py-2.5 transition-colors bg-surface">
              <Search className="w-4 h-4 text-charcoal/50" />
              <input
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="browse-search"
                placeholder={mode === 'category' ? `Search within ${title}…` : 'Search name, colour, occasion, fabric, tag…'}
                autoFocus={mode === 'search'}
                className="flex-1 bg-transparent outline-none text-sm text-charcoal placeholder-charcoal/40"
              />
              {(q || tag) && (
                <button onClick={clearAll} aria-label="Clear" className="p-1 hover:text-maroon transition-colors"><X className="w-4 h-4" /></button>
              )}
            </div>
            {mode === 'search' && (
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link key={c.key} to={`/category/${c.key}`} className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-brandborder hover:border-maroon hover:text-maroon transition-colors">
                    {c.label}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Tag chips */}
          {tagCloud.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-10">
              <span className="overline mr-2"><Tag className="w-3 h-3 inline-block mr-1" />Tags</span>
              <button onClick={() => setTag('')} className={`px-3 py-1 text-[11px] uppercase tracking-[0.15em] border transition-colors ${!tag ? 'border-maroon text-maroon' : 'border-brandborder text-charcoal/60 hover:border-maroon hover:text-maroon'}`}>any</button>
              {tagCloud.map(([t]) => (
                <button key={t} onClick={() => setTag(tag === t ? '' : t)} data-testid={`tag-${t}`}
                  className={`px-3 py-1 text-[11px] uppercase tracking-[0.15em] border transition-colors ${tag === t ? 'border-maroon text-maroon bg-blush' : 'border-brandborder text-charcoal/60 hover:border-maroon hover:text-maroon'}`}>
                  {t}
                </button>
              ))}
            </div>
          )}

          {/* Grid */}
          {loading ? (
            <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center border border-dashed border-brandborder">
              <p className="text-charcoal/60">No pieces match your filters yet.</p>
              {(q || tag) && (
                <button onClick={clearAll} className="mt-4 text-xs uppercase tracking-[0.2em] text-maroon hover:underline">Clear filters</button>
              )}
            </div>
          ) : (
            <AnimatePresence>
              <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                {filtered.map((p, i) => (
                  <motion.div key={p.slug} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, delay: (i % 8) * 0.04 }}>
                    <Link to={`/product/${p.slug}`} data-testid={`browse-card-${p.slug}`} className="group block relative">
                      <div className="relative overflow-hidden">
                        <div className="transition-transform duration-[1200ms] ease-out group-hover:scale-105">
                          <Placeholder label={p.label} ratio="3/4" image={productImageUrl(p)} alt={p.label} sold={!!p.sold} />
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-burgundy/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-ivory/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500">
                          <ArrowUpRight className="w-4 h-4 text-maroon" />
                        </div>
                      </div>
                      <div className="pt-4 flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-display text-lg text-charcoal tracking-tight">{p.label}</h3>
                          {p.sub && <p className="text-xs text-charcoal/60 mt-1 line-clamp-2">{p.sub}</p>}
                          {mode === 'search' && (
                            <p className="text-[10px] uppercase tracking-[0.18em] text-charcoal/45 mt-1">
                              {categories.find((c) => c.key === p.category)?.label || p.category}
                            </p>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          )}

          {/* Glimpse note */}
          <p className="mt-16 max-w-3xl text-sm lg:text-base text-charcoal/70 italic font-display border-l-2 border-gold pl-5">
            What you see here is only a glimpse of what we offer. Stop by our boutique to
            discover our complete collection and find the perfect outfit for your special
            occasion.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-charcoal/70 hover:text-maroon transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>
            {mode === 'category' && (
              <button onClick={() => nav('/search')} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-charcoal/70 hover:text-maroon transition-colors">
                <Search className="w-4 h-4" /> Search all collections
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Related collections — internal linking for SEO */}
      {aliasSeo && (
        <section className="border-t border-brandborder/60 py-14 lg:py-20 bg-surface">
          <div className="container-luxe">
            <span className="overline">Explore more</span>
            <h2 className="font-display text-2xl lg:text-3xl text-charcoal mt-3">
              Related Indian ethnic wear collections
            </h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {[
                { path: '/lehengas', label: 'Designer Lehengas' },
                { path: '/sarees', label: 'Indian Sarees' },
                { path: '/bridal-wear', label: 'Bridal Wear' },
                { path: '/kurti-sets', label: 'Kurti Sets' },
                { path: '/shararas', label: 'Sharara Suits' },
                { path: '/gowns', label: 'Party Wear Gowns' },
                { path: '/men', label: "Men's Kurta Pajama" },
                { path: '/kids', label: 'Kids Indian Clothing' },
                { path: '/plus-size', label: 'Plus Size Indian Clothing' },
                { path: '/indian-clothing-usa', label: 'Ships across the USA' },
              ]
                .filter((it) => `/${aliasKey}` !== it.path)
                .slice(0, 10)
                .map((it) => (
                  <Link
                    key={it.path}
                    to={it.path}
                    data-testid={`related-${it.path.slice(1)}`}
                    className="border border-brandborder p-4 text-center text-xs uppercase tracking-[0.16em] text-charcoal/75 hover:border-maroon hover:text-maroon transition-colors"
                  >
                    {it.label}
                  </Link>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* Page FAQ — for both users and Google FAQ rich results */}
      {aliasSeo?.faqs?.length ? (
        <PageFAQ items={aliasSeo.faqs} title="Frequently asked" testId="category-faq" />
      ) : null}

      <Footer />
      <FloatingActions />
    </main>
  );
}
